import React from "react";
import { message } from "antd";
import _ from "lodash";
import classnames from "classnames";
import moment from "moment";

import { List, FormEditableCell } from "../client-common";
import { CardSubmitParams, CardFieldProps } from "../typings";
import clientStyle from "../index.less";
import { spacePhoneCheckApi } from "../../client-card/api";

const disabledKeyList = [
  "created_on",
  "updated_on",
  "latest_conv_on",
  "latest_visit_on",
];

const getSafeHttpUrl = (url?: string) => {
  const trimmed = url?.trim();
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:"
      ? parsed.href
      : null;
  } catch {
    return null;
  }
};

type CustomerAttr = Record<string, string>;

type CardProps = {
  trackId: string;
  convId: number;
  attrs: CustomerAttr;
  cardField: Array<CardFieldProps>;
  disabledKeydown?: boolean;
  updateAttr: (attrs: CardSubmitParams) => Promise<void>;
};
type ReactEvent = React.KeyboardEvent<HTMLElement>;

const Card = ({
  attrs,
  updateAttr,
  cardField,
  trackId,
  convId,
  disabledKeydown,
}: CardProps): JSX.Element => {

  const update = (params: CardSubmitParams, successCb: () => void) => {
    const name = Object.keys(params)[0];
    // 值没有改变则不进行更新
    if (_.isEqual(attrs[name] || "", params[name] || "")) {
      successCb();
      return;
    }
    const hasAge = name === "age";
    // const hasName = name === 'name';
    const result: CardSubmitParams = { ...params };
    // 年龄做特殊判断
    if (hasAge) {
      const age = result[name];
      const parseValue = Number.parseInt(`${age}`, 10);
      if (Number.isNaN(parseValue)) {
        message.error("请输入正确的年龄，范围0-127");
        return;
      }
      if (parseValue < 0 || parseValue > 127) {
        message.error("请输入正确的年龄，范围0-127");
        return;
      }
      result[name] = parseValue;
    }
    // 把老的名字也传出去，在修改记录里面需要
    // if (hasName) {
    //   result.oldName = attrs[name];
    // }
    updateAttr(result).then(() => {
      if (successCb) {
        successCb();
        message.success("更新成功!");
      }
    });
  };

  const handlePhoneSpaceCheck = (tel: string) => {
    spacePhoneCheckApi({ conv_id: convId, track_id: trackId, tel: tel })
      .then((res) => {
        if (res?.data) {
          // dispatch(updateCustomerAttrs(trackId, { phone_result: res.data }));
          // updateAttr({ phone_result: res.data });
        }
      })
      .catch((res) => {
        if (res?.data?.message) {
          message.info(res.data?.message);
        }
      });
  };

  const getValue = (name: string, type: CardFieldTypeList) => {
    const content = attrs[name];
    if (disabledKeyList.includes(name)) {
      return content
        ? moment(moment.utc(content).toDate()).format("YYYY-MM-DD HH:mm:ss")
        : "";
    }
    if (type === "check" && _.isString(attrs[name])) {
      return attrs[name].split(", ");
    }
    return attrs[name];
  };

  const dataSetting = cardField
    .filter((val: CardFieldProps) => val.name !== "weixin_url")
    .map((val: CardFieldProps) => {
      const { label, name, display, type, metainfo, extra, display_name } = val;
      const value = getValue(name, type);
      let disabledEdit = true;
      if (disabledEdit) {
        disabledEdit = !disabledKeyList.includes(name);
      }
      return {
        label: display_name || label || name,
        name,
        initialValue: value || "",
        editable: display && disabledEdit,
        type,
        selectData: metainfo || [],
        extra,
      };
    });
  const onKeyDownFocus = (e: ReactEvent) => {
    return e.keyCode === 78;
  };

  const dataTrackId = {
    label: "访问标识",
    children: trackId,
  };
  const weixinQRCodeUrl = getSafeHttpUrl(attrs.weixin_url);
  const weixinQRCodeData = weixinQRCodeUrl
    ? {
        label: "微信二维码",
        children: (
          <a href={weixinQRCodeUrl} target="_blank" rel="noopener noreferrer">
            {"图片链接"}
          </a>
        ),
      }
    : null;

  //  校验值是否合法
  const filterValue = ({
    type,
    initialValue,
  }: {
    type: CardFieldTypeList;
    initialValue: CardFiledValue;
  }) => {
    const isTextOrNumber = (val: CardFiledValue) =>
      _.isString(val) || _.isNumber(val);
    const isArray = (val: CardFiledValue) => Array.isArray(val);
    // 如果没有选值的时候
    const isEmptyStringOrArray = (val: CardFiledValue) =>
      val === "" || isArray(val);

    const validateMap = {
      string: isTextOrNumber,
      text: isTextOrNumber,
      radio: isTextOrNumber,
      check: isEmptyStringOrArray,
      checkbox: isArray,
      datetime: () => true,
      date: () => true,
      number: isTextOrNumber,
    };

    const validate = validateMap[type];
    if (validate) {
      return validate(initialValue);
    }

    return true;
  };

  const data = dataSetting
    .filter((setting) => filterValue(setting))
    .map((val, index) => {
      const { label, type, extra, name, ...rest } = val;
      const isComment = val.name === "comment";
      const extraProps =
        isComment && !disabledKeydown ? { onKeyDownFocus } : null;
      const customProps = {
        type,
        name,
        ...rest,
      };

      return {
        label,
        children: (
          <div style={{ display: "flex", alignItems: "center" }}>
            <FormEditableCell
              {...customProps}
              {...extraProps}
              onSave={update}
              key={`card${index}`}
            />
            {extra}
          </div>
        ),
      };
    });

  return (
    <div id="card" className={classnames(clientStyle.tabpane)}>
      <List
        data={
          weixinQRCodeData
            ? [...data, weixinQRCodeData, dataTrackId]
            : [...data, dataTrackId]
        }
        attrs={attrs}
        onSpacePhoneCheck={handlePhoneSpaceCheck}
      />
    </div>
  );
};

export default Card;
